
import Vue from "vue";
import moment, { Moment } from "moment";
import eventBus from "@/eventBus";
import MonthControl from "@/components/MonthControl.vue";
import ResultSummary from "@/components/ResultSummary.vue";
import ResultOne from "@/components/ResultOne.vue";
import ResultList from "@/components/ResultList.vue";
import AppLoading from "@/components/AppLoading.vue";
import ResultCalendar from "@/components/ResultCalendar.vue";
import GlobalOrderPathFilter from "@/components/GlobalOrderPathFilter.vue";
import DailyAmountCountPaymentChart from "@/components/DailyAmountCountPaymentChart.vue";
import DailyCountChart from "@/components/DailyCountChart.vue";
import DailyAovChart from "@/components/DailyAovChart.vue";
import DailyPgFeeChart from "@/components/DailyPgFeeChart.vue";
import {
  fetchSettlementData,
  getResultsFromDb,
} from "@/services/settlementService";
import { db } from "@/utils/dexie";
import { loadPgFeeRates, summarizePgFees, PgFeeSummary } from "@/utils/pgFee";
import { summarizeOrderPayments } from "@/utils/orderPaymentSummary";
import { summarizeOrderSpecifiedPaymentTotal } from "@/utils/orderSpecifiedPaymentTotal";
import { summarizeRefundAmounts } from "@/utils/refundAmountSummary";
import {
  collectOrderPathOptions,
  filterSettlementResult,
  getDefaultOrderPathOptions,
  OrderPathFilterMode,
  OrderPathPresentationMode,
} from "@/utils/orderPathFilter";
import {
  formatSettledElapsedTime,
  getLatestSettledTimeForMonth,
  SettledElapsedTimeInfo,
} from "@/utils/settlementTimeTracker";

moment.locale("ko");

const ORDER_PATH_FILTER_STORAGE_PREFIX = "st-admin:order-path-filter";

export default Vue.extend({
  name: "SettlementView",
  components: {
    MonthControl,
    ResultSummary,
    ResultOne,
    ResultList,
    AppLoading,
    GlobalOrderPathFilter,
    ResultCalendar,
    DailyCountChart,
    DailyAmountCountPaymentChart,
    DailyAovChart,
    DailyPgFeeChart,
  },
  data: () => ({
    activeTab: 0,
    summaryLoading: false,
    timeNow: Date.now(),
    timeTimer: null as any,
    month_payment_composition_amount: 0,
    month_order_count: 0,
    month_payment_summary: {
      orderCount: 0,
      itemCount: 0,
      productAmount: 0,
      shippingAmount: 0,
      discountAmount: 0,
      couponAmount: 0,
      orderCompositionAmount: 0,
      paymentAmount: 0,
    },
    month_refund_amount: 0,
    month_refund_count: 0,
    month_refund_breakdown: {} as Record<string, number>,
    month_orders: [] as any[],
    month_refunds: [] as any[],
    month_fee_summary: {
      fee: 0,
      exactCount: 0,
      estimatedCount: 0,
      unavailableCount: 0,
      orderCount: 0,
    } as PgFeeSummary,
    weeks: [] as Array<any>,
    selectedRange: [] as Array<string>,
    calendarValue: moment(),
    activePreset: null as string | null,
    results: [] as any[],
    latestQueryResult: null as any,
    querySequence: 0,
    activeQueryController: null as AbortController | null,
    lastAutoQueryGeneration: -1,
    orderPathFilterMode: "include" as OrderPathFilterMode,
    orderPathPresentationMode: "grouped" as OrderPathPresentationMode,
    selectedOrderPathKeys: [] as string[],
    pendingRouteAction: null as (() => Promise<void> | void) | null,
    skipNextDetailRoute: null as string | null,
    resultsDbPromise: null as Promise<void> | null,
  }),
  watch: {
    $route: {
      handler(to, from) {
        if (to) void this.applyRoute(to, from);
      },
      deep: true,
    },
    activeTab() {
      this.refreshActiveTabComponents();
    },
    operationalReady(ready: boolean) {
      if (ready) {
        if (this.pendingRouteAction) {
          const action = this.pendingRouteAction;
          this.pendingRouteAction = null;
          void action();
        } else {
          void this.maybeAutoQuery();
        }
      }
    },
    mallInfoKey() {
      this.restoreOrderPathFilter();
      void this.loadResultsFromDb();
    },
    selectedOrderPathKeys() {
      this.persistOrderPathFilter();
      this.refreshActiveTabComponents();
    },
    orderPathFilterMode() {
      this.persistOrderPathFilter();
      this.refreshActiveTabComponents();
    },
    orderPathPresentationMode() {
      this.persistOrderPathFilter();
      eventBus.$emit(
        "order-path-presentation-mode-updated",
        this.orderPathPresentationMode
      );
      this.refreshActiveTabComponents();
    },
  },
  computed: {
    operationalReady(): boolean {
      return this.$store.state.operationalReady;
    },
    mallInfoKey(): string {
      const info = this.$store.state.mall_info;
      return info ? `${info.mall_id}:${info.shop_no}` : "";
    },
    currentMonthStr(): string {
      return (this.calendarValue as Moment).format("YYYY-MM");
    },
    currentMonthSettledTimestamp(): number | null {
      const mallId = this.$store.state.mall_info?.mall_id;
      const shopNo = this.$store.state.mall_info?.shop_no;
      if (!mallId || shopNo === undefined) return null;

      let latestQueryResultTime: number | null = null;
      if (
        this.latestQueryResult?.updated_at &&
        this.latestQueryResult.start_date?.startsWith(this.currentMonthStr)
      ) {
        latestQueryResultTime = this.latestQueryResult.updated_at;
      }

      const monthSettledTime = getLatestSettledTimeForMonth(
        mallId,
        shopNo,
        this.currentMonthStr,
        this.results
      );

      if (latestQueryResultTime && monthSettledTime) {
        return Math.max(latestQueryResultTime, monthSettledTime);
      }
      return latestQueryResultTime || monthSettledTime;
    },
    currentMonthSettledInfo(): SettledElapsedTimeInfo {
      return formatSettledElapsedTime(
        this.currentMonthSettledTimestamp,
        this.timeNow
      );
    },
    currentTabPath(): string {
      return this.activeTab === 1 ? "chart" : "calendar";
    },
    queryMonthLabel(): string {
      return "정산하기";
    },
    monthStartDate(): string {
      const base = this.calendarValue as Moment;
      return base.clone().startOf("month").format("YYYY-MM-DD");
    },
    monthEndDate(): string {
      const base = this.calendarValue as Moment;
      const end = base.clone().endOf("month");
      return end.isAfter(moment(), "day")
        ? moment().format("YYYY-MM-DD")
        : end.format("YYYY-MM-DD");
    },
    orderPathOptions() {
      const items: any[] = [];
      this.results.forEach((result) => {
        if (Array.isArray(result.orders)) items.push(...result.orders);
        if (Array.isArray(result.refunds)) items.push(...result.refunds);
      });
      if (Array.isArray(this.latestQueryResult?.orders)) {
        items.push(...this.latestQueryResult.orders);
      }
      if (Array.isArray(this.latestQueryResult?.refunds)) {
        items.push(...this.latestQueryResult.refunds);
      }
      return collectOrderPathOptions(items, this.orderPathPresentationMode);
    },
    orderPathFilter() {
      return {
        mode: this.orderPathFilterMode,
        selectedKeys: this.selectedOrderPathKeys,
        presentationMode: this.orderPathPresentationMode,
      };
    },
  },
  methods: {
    switchTab(tabIndex: number) {
      if (this.activeTab !== tabIndex) {
        this.activeTab = tabIndex;
        this.syncBaseRoute("push");
      }
    },
    syncBaseRoute(mode: "push" | "replace" = "push") {
      const targetPath = `/${this.currentTabPath}/${this.currentMonthStr}`;
      if (this.$route.path !== targetPath) {
        if (mode === "replace") {
          this.$router.replace(targetPath).catch((err) => err);
        } else {
          this.$router.push(targetPath).catch((err) => err);
        }
      }
    },
    async updateMonthSummary() {
      this.summaryLoading = true;
      try {
        const mallId = this.$store.state.mall_info?.mall_id;
        const shopNo = this.$store.state.mall_info?.shop_no;
        if (!mallId || !shopNo) return;

        const start = (this.calendarValue as Moment)
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const end = (this.calendarValue as Moment)
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");

        const rows = await db.results
          .where("[mall_id+shop_no+date]")
          .between([mallId, shopNo, start], [mallId, shopNo, end], true, true)
          .toArray();

        const monthOrders: any[] = [];
        const monthItems: any[] = [];
        const monthRefunds: any[] = [];

        for (const row of rows) {
          const filtered = filterSettlementResult(row, this.orderPathFilter);
          if (Array.isArray(filtered.orders)) {
            monthOrders.push(...filtered.orders);
            monthItems.push(...filtered.orders);
          }
          if (Array.isArray(filtered.refunds)) {
            monthItems.push(...filtered.refunds);
            monthRefunds.push(...filtered.refunds);
          }
        }

        this.month_payment_summary = summarizeOrderPayments(monthOrders);
        this.month_orders = monthOrders;
        this.month_order_count = this.month_payment_summary.orderCount;
        this.month_payment_composition_amount =
          summarizeOrderSpecifiedPaymentTotal(monthOrders).totalAmount;
        this.month_refund_count = monthRefunds.length;
        this.month_refunds = monthRefunds;
        const refundSummary = summarizeRefundAmounts(monthRefunds);
        this.month_refund_amount = refundSummary.amount;
        this.month_refund_breakdown = refundSummary.breakdown;
        this.month_fee_summary = summarizePgFees(
          monthItems,
          loadPgFeeRates(),
          this.orderPathPresentationMode
        );
      } catch (e) {
        console.error("updateMonthSummary error:", e);
      } finally {
        this.summaryLoading = false;
      }
    },
    openOnboarding() {
      eventBus.$emit("open-onboarding");
    },
    openSavedResultList() {
      const target = `/saved-results/${this.currentMonthStr}`;
      if (this.$route.path !== target) {
        this.$router.push(target).catch((err) => err);
      }
      (this.$refs.resultList as any)?.open?.();
    },
    openPgFeeSettings(unconfiguredItems?: any, targetTab?: string) {
      eventBus.$emit(
        "open-pg-fee-settings",
        unconfiguredItems,
        targetTab,
        this.orderPathPresentationMode
      );
    },
    handleOpenOrders(payload: {
      startDate: string;
      endDate: string;
      place?: string;
      method?: string;
      refund?: string;
      updateRoute?: boolean;
    }) {
      if (payload.updateRoute !== false) {
        const query: Record<string, string> = {};
        if (payload.place) {
          query.place = payload.place;
        }
        if (payload.method) {
          query.method = payload.method;
        }
        if (payload.refund) {
          query.refund = payload.refund;
        }
        const targetPath =
          payload.startDate === payload.endDate
            ? `/orders/${payload.startDate}`
            : `/orders/${payload.startDate}/${payload.endDate}`;
        this.$router.push({ path: targetPath, query }).catch((err) => err);
      }
    },
    handleCloseOrders(payload: { startDate: string; endDate: string }) {
      const targetPath =
        payload.startDate === payload.endDate
          ? `/detail/${payload.startDate}`
          : `/detail/${payload.startDate}/${payload.endDate}`;
      if (this.$route.path !== targetPath) {
        this.$router.push(targetPath).catch((err) => err);
      }
    },
    handleDetailClose() {
      if (
        this.$route.path.startsWith("/detail") ||
        this.$route.path.startsWith("/orders")
      ) {
        this.syncBaseRoute("push");
      }
    },
    handleResultListClose() {
      if (this.$route.path.startsWith("/saved-results")) {
        this.syncBaseRoute("push");
      }
    },
    getOrderPathFilterStorageKey(): string {
      const info = this.$store.state.mall_info;
      if (!info?.mall_id || info.shop_no === undefined) return "";
      return `${ORDER_PATH_FILTER_STORAGE_PREFIX}:${info.mall_id}:${info.shop_no}`;
    },
    persistOrderPathFilter() {
      if (typeof window === "undefined") return;
      const key = this.getOrderPathFilterStorageKey();
      if (!key) return;

      try {
        window.localStorage.setItem(
          key,
          JSON.stringify({
            mode: this.orderPathFilterMode,
            selectedKeys: this.selectedOrderPathKeys,
            presentationMode: this.orderPathPresentationMode,
          })
        );
      } catch (e) {
        console.warn("Failed to save order path filter:", e);
      }
    },
    restoreOrderPathFilter() {
      if (typeof window === "undefined") return;
      const key = this.getOrderPathFilterStorageKey();
      if (!key) return;

      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
          this.orderPathFilterMode = "include";
          this.orderPathPresentationMode = "grouped";
          this.selectedOrderPathKeys = [];
          return;
        }
        const saved = raw ? JSON.parse(raw) : null;
        this.orderPathFilterMode =
          saved?.mode === "exclude" ? "exclude" : "include";
        this.orderPathPresentationMode =
          saved?.presentationMode === "separate" ? "separate" : "grouped";
        this.selectedOrderPathKeys = Array.isArray(saved?.selectedKeys)
          ? Array.from(
              new Set(
                saved.selectedKeys.filter(
                  (value: unknown): value is string =>
                    typeof value === "string" && value.length > 0
                )
              )
            )
          : [];
      } catch (e) {
        console.warn("Failed to restore order path filter:", e);
        this.orderPathFilterMode = "include";
        this.orderPathPresentationMode = "grouped";
        this.selectedOrderPathKeys = [];
      }
    },
    applyDefaultOrderPathFilter() {
      if (typeof window === "undefined") return;
      const key = this.getOrderPathFilterStorageKey();
      if (!key || window.localStorage.getItem(key)) return;

      const defaultKeys = getDefaultOrderPathOptions(
        this.orderPathPresentationMode
      ).map((option) => option.key);

      this.orderPathFilterMode = "include";
      this.selectedOrderPathKeys = defaultKeys;
      this.persistOrderPathFilter();
    },
    openMonthDetail(start?: string, end?: string) {
      const start_date =
        typeof start === "string" ? start : this.monthStartDate;
      const end_date = typeof end === "string" ? end : this.monthEndDate;
      this.query(start_date, end_date, false);
    },
    queryMonth() {
      if (this.currentMonthSettledInfo.isJustSettled) {
        eventBus.$emit("snackbar", {
          message:
            "방금 정산을 완료한 최신 데이터입니다. 약 1분 후 다시 정산할 수 있습니다.",
        });
        return;
      }
      const base = (this.calendarValue as Moment) || moment();
      const start_date = base.clone().startOf("month").format("YYYY-MM-DD");
      const end_date = base.clone().endOf("month").format("YYYY-MM-DD");
      this.query(start_date, end_date, true);
    },
    queryCurrentMonth(openModal = false) {
      if (this.currentMonthSettledInfo.isJustSettled) {
        eventBus.$emit("snackbar", {
          message:
            "방금 정산을 완료한 최신 데이터입니다. 약 1분 후 다시 정산할 수 있습니다.",
        });
        return;
      }
      const base = (this.calendarValue as Moment) || moment();
      const start_date = base.clone().startOf("month").format("YYYY-MM-DD");
      const end_date = base.clone().endOf("month").format("YYYY-MM-DD");
      this.query(start_date, end_date, true, openModal);
    },
    setPreset(type: string) {
      this.activePreset = type;
      if (type === "today") {
        const today = moment().format("YYYY-MM-DD");
        this.query(today, today);
      } else if (type === "thisWeek") {
        const startOfWeek = moment().startOf("week").format("YYYY-MM-DD");
        const endOfWeek = moment().endOf("week").format("YYYY-MM-DD");
        this.query(startOfWeek, endOfWeek);
      } else if (type === "thisMonth") {
        const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
        const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");
        this.query(startOfMonth, endOfMonth);
      } else if (type === "lastMonth") {
        const lm = moment().subtract(1, "month");
        const startOfLastMonth = lm
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const endOfLastMonth = lm.clone().endOf("month").format("YYYY-MM-DD");
        this.query(startOfLastMonth, endOfLastMonth);
      }
    },
    refreshActiveTabComponents() {
      this.updateMonthSummary();
      this.$nextTick(() => {
        if (this.activeTab === 0) {
          (this.$refs.resultCalendar as any)?.render?.();
        } else if (this.activeTab === 1) {
          (this.$refs.dailyAmountCountPaymentChart as any)?.render?.();
          (this.$refs.dailyCountChart as any)?.render?.();
          (this.$refs.dailyAovChart as any)?.render?.();
          (this.$refs.dailyPgFeeChart as any)?.render?.();
        }
      });
    },
    updateCalendarValue(date: Moment) {
      this.calendarValue = date;
      this.updateMonthSummary();
    },
    hasCachedSettlementRange(startDate: string, endDate: string): boolean {
      const start = moment(startDate, "YYYY-MM-DD", true);
      const end = moment(endDate, "YYYY-MM-DD", true);
      const today = moment().startOf("day");

      // Today's settlement is intentionally refreshed instead of read from cache.
      if (
        !start.isValid() ||
        !end.isValid() ||
        end.isBefore(start) ||
        !end.isBefore(today)
      ) {
        return false;
      }

      const cachedDates = new Set(this.results.map((result) => result.date));
      for (const date = start.clone(); !date.isAfter(end); date.add(1, "day")) {
        if (!cachedDates.has(date.format("YYYY-MM-DD"))) return false;
      }
      return true;
    },
    async loadResultsFromDb() {
      const mallId = this.$store.state.mall_info?.mall_id;
      const shopNo = this.$store.state.mall_info?.shop_no;
      if (mallId && shopNo) {
        this.resultsDbPromise = (async () => {
          this.results = await getResultsFromDb(mallId, shopNo);
          this.applyDefaultOrderPathFilter();
          await this.updateMonthSummary();
        })();
        await this.resultsDbPromise;
      }
    },
    isMonthFullySettled(date: Moment): boolean {
      const today = moment().startOf("day");
      const startOfMonth = date.clone().startOf("month");
      const endOfMonth = date.clone().endOf("month");

      // 1. 미래의 월인 경우: 아직 정산 가능한 날짜가 없으므로 추가 정산 불필요
      if (startOfMonth.isAfter(today)) {
        return true;
      }

      // 2. 과거 날짜(월초 ~ 어제 또는 말일) 중 캐시에 누락된 일자가 있는지 확인
      const cachedDates = new Set(
        this.results.map((r) => r.date).filter(Boolean)
      );
      const yesterday = today.clone().subtract(1, "day");
      const pastEnd = endOfMonth.isBefore(yesterday) ? endOfMonth : yesterday;

      if (!pastEnd.isBefore(startOfMonth)) {
        for (
          const cur = startOfMonth.clone();
          !cur.isAfter(pastEnd);
          cur.add(1, "day")
        ) {
          if (!cachedDates.has(cur.format("YYYY-MM-DD"))) {
            return false;
          }
        }
      }

      // 3. 과거 월인 경우(말일이 오늘 이전): 과거 날짜가 모두 캐시되어 있으면 정산 완료
      if (endOfMonth.isBefore(today)) {
        return true;
      }

      // 4. 현재 진행 중인 월(이번 달)인 경우:
      // 오늘(00:00 이후) 해당 월의 정산이 최소 1회 이상 수행되었는지 확인
      const mallId = this.$store.state.mall_info?.mall_id;
      const shopNo = this.$store.state.mall_info?.shop_no;
      const targetMonthStr = date.format("YYYY-MM");

      let latestSettledTime: number | null = null;
      if (
        this.latestQueryResult?.updated_at &&
        this.latestQueryResult.start_date?.startsWith(targetMonthStr)
      ) {
        latestSettledTime = this.latestQueryResult.updated_at;
      }
      if (mallId && shopNo !== undefined) {
        const monthSettledTime = getLatestSettledTimeForMonth(
          mallId,
          shopNo,
          targetMonthStr,
          this.results
        );
        if (latestSettledTime && monthSettledTime) {
          latestSettledTime = Math.max(latestSettledTime, monthSettledTime);
        } else if (!latestSettledTime) {
          latestSettledTime = monthSettledTime;
        }
      }

      if (!latestSettledTime) {
        return false;
      }

      // 오늘 0시 이후에 정산된 이력이 있으면 당일 최신 상태로 간주
      return moment(latestSettledTime).isSameOrAfter(today);
    },
    async checkAndAutoQuery(date: Moment) {
      const mallId = this.$store.state.mall_info?.mall_id;
      const shopNo = this.$store.state.mall_info?.shop_no;
      if (!mallId || !shopNo || !this.operationalReady) return;

      const isFullySettled = this.isMonthFullySettled(date);

      if (!isFullySettled) {
        const startOfMonth = date.clone().startOf("month").format("YYYY-MM-DD");
        const endOfMonth = date.clone().endOf("month").format("YYYY-MM-DD");
        await this.query(startOfMonth, endOfMonth, false, false, true, true);
      } else {
        this.refreshActiveTabComponents();
      }
    },
    async maybeAutoQuery() {
      const generation = this.$store.state.authGeneration;
      const routePath = this.$route?.path || "";
      if (
        !this.operationalReady ||
        !this.mallInfoKey ||
        routePath.startsWith("/detail") ||
        routePath.startsWith("/orders") ||
        this.lastAutoQueryGeneration === generation
      ) {
        return;
      }
      this.lastAutoQueryGeneration = generation;
      if (this.resultsDbPromise) {
        await this.resultsDbPromise;
      }
      await this.checkAndAutoQuery(this.calendarValue);
    },
    handleAuthSessionReset() {
      if (this.activeQueryController) {
        this.activeQueryController.abort();
        this.activeQueryController = null;
      }
      this.resultsDbPromise = null;
      this.lastAutoQueryGeneration = -1;
      this.results = [];
      this.latestQueryResult = null;
      this.orderPathFilterMode = "include";
      this.orderPathPresentationMode = "grouped";
      this.selectedOrderPathKeys = [];
      (this.$refs.appLoading as any)?.setLoading(false);
    },
    async query(
      start_date: string,
      end_date: string,
      refresh = false,
      openModal = true,
      showLoading = true,
      immediateLoading = false,
      autoOrderFilter?: { place?: string; method?: string; refund?: string }
    ) {
      const mallInfo = this.$store.state.mall_info;
      if (!this.operationalReady || !mallInfo) {
        eventBus.$emit("snackbar", {
          message: "상점 정보를 준비 중입니다. 잠시 후 다시 시도해주세요.",
        });
        return;
      }

      const loadingRef = this.$refs.appLoading as any;
      const modalRef = this.$refs.resultOne as any;
      const shouldShowLoading =
        showLoading &&
        (refresh || !this.hasCachedSettlementRange(start_date, end_date));

      if (this.activeQueryController) {
        this.activeQueryController.abort();
        this.activeQueryController = null;
        loadingRef?.setLoading(false);
      }

      const controller = new AbortController();
      const queryId = this.querySequence + 1;
      this.querySequence = queryId;
      this.activeQueryController = controller;

      if (shouldShowLoading) {
        loadingRef?.setLoading(true, "", "refresh", immediateLoading);
      }

      try {
        const { processedResult } = await fetchSettlementData({
          axios: this.$axios,
          mallId: mallInfo.mall_id,
          shopNo: mallInfo.shop_no,
          startDate: start_date,
          endDate: end_date,
          refresh,
          signal: controller.signal,
          onProgress: (msg: string, mode?: "refresh" | "settlement") => {
            if (
              shouldShowLoading &&
              this.querySequence === queryId &&
              this.activeQueryController === controller &&
              !controller.signal.aborted
            ) {
              loadingRef?.setLoading(true, msg, mode);
            }
          },
          onNotice: (msg: string) => {
            if (
              this.querySequence === queryId &&
              this.activeQueryController === controller &&
              !controller.signal.aborted
            ) {
              eventBus.$emit("snackbar", { message: msg });
            }
          },
        });

        if (
          controller.signal.aborted ||
          this.querySequence !== queryId ||
          this.activeQueryController !== controller
        ) {
          return;
        }

        const shouldUpdateOpenModal =
          openModal ||
          (modalRef?.dialog &&
            modalRef?.hasDisplayedResult?.(
              mallInfo.mall_id,
              mallInfo.shop_no,
              start_date,
              end_date
            ));

        if (shouldUpdateOpenModal) {
          modalRef?.open(
            JSON.parse(JSON.stringify(processedResult)),
            autoOrderFilter
          );
          if (
            openModal &&
            (!this.$route.path.startsWith("/orders") ||
              autoOrderFilter === undefined)
          ) {
            const detailPath =
              start_date === end_date
                ? `/detail/${start_date}`
                : `/detail/${start_date}/${end_date}`;
            if (this.$route.path !== detailPath) {
              // This query already has the result to show. Let the route watcher
              // update route state without starting the same detail query again.
              this.skipNextDetailRoute = detailPath;
              this.$router.push(detailPath).catch((err) => err);
            }
          }
        }

        this.latestQueryResult = processedResult;
        await this.loadResultsFromDb();
        this.refreshActiveTabComponents();

        if (refresh && !openModal) {
          eventBus.$emit("snackbar", {
            message: "정산 데이터를 새로 불러왔습니다.",
          });
        }
      } catch (e) {
        if (
          controller.signal.aborted ||
          this.querySequence !== queryId ||
          (e as any)?.code === "ERR_CANCELED" ||
          (e as any)?.name === "CanceledError"
        ) {
          return;
        }
        console.error("Settlement Query Error:", e);
        eventBus.$emit("snackbar", {
          message: "정산 진행 중 오류가 발생했습니다.",
        });
      } finally {
        if (this.activeQueryController === controller) {
          this.activeQueryController = null;
        }
        if (
          shouldShowLoading &&
          this.querySequence === queryId &&
          this.activeQueryController === null
        ) {
          loadingRef?.setLoading(false);
        }
      }
    },
    async applyRoute(toRoute?: any, from?: any) {
      const to = toRoute || this.$route;
      const path = to.path || "/";
      const name = to.name;
      const params = to.params || {};
      const query = to.query || {};

      const monthParam = params.month;
      if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)) {
        const m = moment(monthParam, "YYYY-MM");
        if (m.isValid() && m.format("YYYY-MM") !== this.currentMonthStr) {
          this.calendarValue = m;
          await this.loadResultsFromDb();
          if (this.operationalReady) {
            await this.checkAndAutoQuery(m);
          }
        }
      }

      if (name === "ChartView" || path.startsWith("/chart")) {
        if (this.activeTab !== 1) {
          this.activeTab = 1;
        }
      } else if (name === "CalendarView" || path.startsWith("/calendar")) {
        if (this.activeTab !== 0) {
          this.activeTab = 0;
        }
      }

      if (name === "DetailView" || path.startsWith("/detail")) {
        const start = params.startDate;
        const end = params.endDate || start;
        if (start && /^\d{4}-\d{2}-\d{2}$/.test(start)) {
          if (this.skipNextDetailRoute === path) {
            this.skipNextDetailRoute = null;
            return;
          }
          const mallInfo = this.$store.state.mall_info;
          const modalRef = this.$refs.resultOne as any;
          if (
            mallInfo &&
            modalRef?.hasDisplayedResult(
              mallInfo.mall_id,
              mallInfo.shop_no,
              start,
              end
            )
          ) {
            modalRef.syncOrderListRoute();
            return;
          }
          if (this.operationalReady) {
            await this.query(start, end, false, true);
          } else {
            this.pendingRouteAction = () => this.query(start, end, false, true);
          }
        }
      } else if (name === "OrdersView" || path.startsWith("/orders")) {
        const start = params.startDate;
        const end = params.endDate || start;
        const autoOrderFilter = {
          place: query.place ? String(query.place) : undefined,
          method: query.method ? String(query.method) : undefined,
          refund: query.refund ? String(query.refund) : undefined,
        };
        if (start && /^\d{4}-\d{2}-\d{2}$/.test(start)) {
          const mallInfo = this.$store.state.mall_info;
          const modalRef = this.$refs.resultOne as any;
          if (
            mallInfo &&
            modalRef?.hasDisplayedResult(
              mallInfo.mall_id,
              mallInfo.shop_no,
              start,
              end
            )
          ) {
            modalRef.syncOrderListRoute(autoOrderFilter);
            return;
          }
          if (this.operationalReady) {
            await this.query(
              start,
              end,
              false,
              true,
              true,
              false,
              autoOrderFilter
            );
          } else {
            this.pendingRouteAction = () =>
              this.query(start, end, false, true, true, false, autoOrderFilter);
          }
        }
      } else if (
        name === "PgFeeSettings" ||
        path.startsWith("/settings/pg-fee")
      ) {
        const tab = params.tab || "matrix";
        this.$nextTick(() => {
          eventBus.$emit("open-pg-fee-settings", undefined, tab);
        });
      } else if (name === "OnboardingGuide" || path.startsWith("/guide")) {
        this.$nextTick(() => {
          eventBus.$emit("open-onboarding");
        });
      } else if (name === "SavedResults" || path.startsWith("/saved-results")) {
        this.$nextTick(() => {
          (this.$refs.resultList as any)?.open?.();
        });
      } else {
        if (
          from &&
          (from.path.startsWith("/detail") || from.path.startsWith("/orders"))
        ) {
          (this.$refs.resultOne as any)?.closeSilently?.();
        }
        if (from && from.path.startsWith("/saved-results")) {
          (this.$refs.resultList as any)?.closeSilently?.();
        }
        if (path === "/") {
          this.syncBaseRoute("replace");
        }
      }
    },
  },
  async created() {
    this.restoreOrderPathFilter();
    await this.loadResultsFromDb();

    eventBus.$on("update-calendar", async (date: Moment) => {
      this.calendarValue = date;
      if (
        !this.$route.path.startsWith("/detail") &&
        !this.$route.path.startsWith("/orders") &&
        !this.$route.path.startsWith("/settings") &&
        !this.$route.path.startsWith("/guide") &&
        !this.$route.path.startsWith("/saved-results")
      ) {
        this.syncBaseRoute("push");
      }
      await this.checkAndAutoQuery(date);
    });
    eventBus.$on("pg-fee-updated", () => {
      this.refreshActiveTabComponents();
    });
    eventBus.$on("open-detail", (start?: string, end?: string) => {
      this.openMonthDetail(start, end);
    });
    eventBus.$on("open-saved-result-list", this.openSavedResultList);
    eventBus.$on("auth-session-reset", this.handleAuthSessionReset);
    eventBus.$on(
      "query",
      async (
        start_date: string,
        end_date: string,
        refresh = false,
        openModal = true,
        showLoading = true
      ) => {
        await this.query(start_date, end_date, refresh, openModal, showLoading);
      }
    );
  },
  async mounted() {
    this.timeTimer = setInterval(() => {
      this.timeNow = Date.now();
    }, 10000);

    const initialStartDate = this.$route.params.startDate;
    if (initialStartDate && /^\d{4}-\d{2}-\d{2}$/.test(initialStartDate)) {
      const initialMonth = moment(initialStartDate.slice(0, 7), "YYYY-MM");
      if (initialMonth.isValid()) {
        this.calendarValue = initialMonth;
      }
    }

    await this.loadResultsFromDb();
    if (this.$route.path !== "/" && this.$route.path !== "") {
      await this.applyRoute(this.$route);
    } else {
      this.syncBaseRoute("replace");
    }
    await this.maybeAutoQuery();
  },
  beforeDestroy() {
    if (this.timeTimer) {
      clearInterval(this.timeTimer);
      this.timeTimer = null;
    }
    eventBus.$off("update-calendar");
    eventBus.$off("pg-fee-updated");
    eventBus.$off("open-detail");
    eventBus.$off("open-saved-result-list", this.openSavedResultList);
    eventBus.$off("auth-session-reset", this.handleAuthSessionReset);
    eventBus.$off("query");
  },
});
